import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import LandlordSidebar from '../../components/landlord/LandlordSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const fmt = (n) => parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function LandlordRevenue() {
  const [revenue, setRevenue] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/landlord/revenue?months=6'),
      api.get('/landlord/payouts'),
    ]).then(([revRes, payRes]) => {
      setRevenue(revRes.data.revenue);
      setPayouts(payRes.data.payouts);
    }).catch(() => toast.error('Could not load revenue data'))
      .finally(() => setLoading(false));
  }, []);

  const totalYTD = revenue.reduce((sum, r) => sum + r.landlord_share, 0);
  const lastPayout = payouts.find(p => p.status === 'transferred');
  const pendingPayout = payouts.find(p => p.status === 'pending');

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="app-layout">
      <Topbar role="landlord" />
      <div className="dashboard-layout">
        <LandlordSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">Revenue &amp; Payouts</div>
          <div className="page-sub">Track your earnings and payout history across all properties.</div>

          {/* Metric cards */}
          <div className="metric-grid">
            <div className="metric-card">
              <div className="metric-label">Revenue YTD (your share)</div>
              <div className="metric-value sm">${fmt(totalYTD)}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Last payout</div>
              <div className="metric-value green sm">${lastPayout ? fmt(lastPayout.amount) : '0.00'}</div>
              <div className="metric-sub">{lastPayout ? fmtDate(lastPayout.paid_at) : '—'}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Pending payout</div>
              <div className="metric-value accent sm">${pendingPayout ? fmt(pendingPayout.amount) : '0.00'}</div>
              <div className="metric-sub">{pendingPayout ? 'Processing' : 'None pending'}</div>
            </div>
          </div>

          {/* Revenue breakdown table */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Monthly revenue breakdown</div>
              <button className="btn btn-outline btn-sm">Export CSV</button>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Properties</th>
                    <th>Active tenants</th>
                    <th>Gross billing</th>
                    <th>Affordable Telecom fee</th>
                    <th>Your share</th>
                  </tr>
                </thead>
                <tbody>
                  {revenue.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-sub)' }}>No revenue data yet</td></tr>
                  )}
                  {revenue.map((r, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 700 }}>{r.month}</td>
                      <td>{r.properties_active}</td>
                      <td>{r.active_tenants}</td>
                      <td>${fmt(r.gross_billing)}</td>
                      <td style={{ color: 'var(--text-sub)' }}>${fmt(r.bulknet_fee)}</td>
                      <td style={{ fontWeight: 700, color: 'var(--teal)' }}>${fmt(r.landlord_share)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payout history */}
          <div className="card">
            <div className="card-header"><div className="card-title">Payout history</div></div>
            <div>
              {payouts.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-sub)' }}>No payouts yet</div>
              )}
              {payouts.map((p, i) => (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 20px',
                  borderBottom: i < payouts.length - 1 ? '1px solid var(--border)' : 'none',
                  flexWrap: 'wrap', gap: 10
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>
                      {fmtDate(p.paid_at || p.created_at)} payout
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>
                      Period: {fmtDate(p.period_start)} – {fmtDate(p.period_end)}
                      {p.stripe_transfer_id ? ` · ${p.stripe_transfer_id}` : ''}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--teal)' }}>${fmt(p.amount)}</div>
                    <span className={`badge badge-${p.status === 'transferred' ? 'green' : p.status === 'pending' ? 'orange' : 'red'}`}>
                      {p.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
